import { supabaseFetch } from "./supabaseClient";

const DEFAULT_TIMEOUT_MS = 4000;
const PAGE_SIZE = 250;
const MAX_PAGES = 4;

export const NOT_MOVED = Object.freeze({ movedToV2: false, v2Id: null });

/**
 * Reports whether a V1 project's data authority has moved to V2 (the
 * v2_authoritative flag from search_collections_for_v1), plus the V2
 * collection id for deep-linking into the rep portal.
 *
 * Fail-open by design, mirroring v1Lockout.js: any error, a missing flag, an
 * unexpected response shape, a slow/hung request (bounded by timeoutMs), or
 * simply not finding the project all report NOT_MOVED. A Supabase problem
 * must never fence V1 UI — the V2-side backstop trigger is the enforcement
 * wall regardless of what this check says; this only makes the wall visible
 * before a rep starts editing.
 *
 * There is no per-project authority RPC, so this pages through the caller's
 * own project list looking for the matching firebase_project_id. Production
 * scale is well inside one page (186 collections total); the page cap is a
 * guard against pathological accounts, and hitting it fails open.
 */
export async function getProjectAuthority(
  firebaseUid,
  firebaseProjectId,
  { timeoutMs = DEFAULT_TIMEOUT_MS } = {},
) {
  if (!firebaseUid || !firebaseProjectId) return NOT_MOVED;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    for (let page = 0; page < MAX_PAGES; page++) {
      const data = await supabaseFetch("/rpc/search_collections_for_v1", {
        method: "POST",
        body: JSON.stringify({
          p_firebase_uid: firebaseUid,
          p_query: "",
          p_limit: PAGE_SIZE,
          p_offset: page * PAGE_SIZE,
        }),
        signal: controller.signal,
      });
      const results = Array.isArray(data?.results) ? data.results : [];
      const row = results.find(
        (r) => r.firebase_project_id === firebaseProjectId,
      );
      if (row) {
        return { movedToV2: row.v2_authoritative === true, v2Id: row.id ?? null };
      }
      if (results.length < PAGE_SIZE) break;
    }
    return NOT_MOVED;
  } catch (err) {
    console.warn("Project authority check failed open:", err?.message ?? err);
    return NOT_MOVED;
  } finally {
    clearTimeout(timer);
  }
}
